import { useEffect, useRef } from 'react';
import { ErrorToast } from '../components/ErrorToast';
import { PhoneFrame } from '../components/PhoneFrame';
import { Label, Paper } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { tiltFor } from '../design/tokens';
import { ro } from '../i18n/ro';
import type { ErrorCode } from 'shared';
import { uploadAvatar } from '../state/api';
import { useRoom } from '../state/RoomConnection';
import { getPlayerToken } from '../state/storage';
import { AdminPanel } from './AdminPanel';
import { JoinScreen } from './JoinScreen';
import { PlayerScreen } from './PlayerScreen';

/** Errors that mean the room cannot be reached at all. */
const FATAL = new Set<ErrorCode>(['ROOM_NOT_FOUND', 'ROOM_CLOSED']);

/**
 * A phone in a room. Decides between joining, playing and the terminal states,
 * and hangs the admin controls underneath when the viewer holds the admin token.
 */
export function RoomScreen() {
  const room = useRoom();
  const snapshot = room.snapshot;

  useEffect(() => {
    const el = document.documentElement;
    el.dataset.phase = snapshot?.phase ?? 'CONNECTING';
    el.dataset.version = String(snapshot?.version ?? 0);
    el.dataset.playerId = snapshot?.viewer.playerId ?? '';
  }, [snapshot?.phase, snapshot?.version, snapshot?.viewer.playerId]);

  // A photo chosen during the join flow is uploaded once the join issues a
  // player token, which is what the upload authenticates with.
  const pendingAvatar = useRef<Blob | null>(null);
  useEffect(() => {
    const blob = pendingAvatar.current;
    if (!blob || !room.playerId) return;
    const token = getPlayerToken(room.roomToken);
    if (!token) return;
    pendingAvatar.current = null;
    void uploadAvatar(room.roomToken, token, blob).catch(() => {
      /* The player keeps their initials; not worth blocking the game. */
    });
  }, [room.playerId, room.roomToken]);

  // A handshake that failed outright is terminal: without surfacing it, an
  // unknown or expired room link just says "connecting" forever.
  if (!snapshot && room.error && FATAL.has(room.error.code)) {
    return (
      <Centered>
        <Stamp tone="red" size="md" className={tiltFor('fatal')}>
          {ro.errors[room.error.code] ?? ro.roomNotFound}
        </Stamp>
      </Centered>
    );
  }

  if (!snapshot) {
    return (
      <Centered>
        <Label className="text-sm text-ink-soft">
          {room.status === 'closed' ? ro.reconnecting : ro.connecting}
        </Label>
      </Centered>
    );
  }

  if (snapshot.phase === 'CLOSED') {
    return (
      <Centered>
        <Stamp tone="red" size="md" className={tiltFor('closed')}>
          {ro.roomClosed}
        </Stamp>
        <p className="mt-4 text-sm text-ink-soft">{ro.roomClosedBody}</p>
      </Centered>
    );
  }

  const isPlayer = snapshot.viewer.playerId !== null;
  const isAdmin = snapshot.viewer.isAdmin;

  /*
   * Being unable to join is not the same as having been removed. A device that
   * *holds* a player token which no longer resolves was kicked; one with no
   * token simply arrived after joining closed, and must not be told it was
   * thrown out.
   */
  const wasKicked = !isPlayer && !isAdmin && getPlayerToken(room.roomToken) !== null;

  return (
    <>
      <PhoneFrame failedElections={snapshot.failedElections}>
        {isPlayer ? (
          <PlayerScreen snapshot={snapshot} />
        ) : snapshot.permissions.canJoin ? (
          <JoinScreen onPendingAvatar={(blob) => (pendingAvatar.current = blob)} />
        ) : wasKicked ? (
          <main className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <Stamp tone="red" size="md" className={tiltFor('kicked')}>
              {ro.kickedYou}
            </Stamp>
          </main>
        ) : (
          // A non-playing admin, or someone who arrived after joining closed.
          <main className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <Label className="text-[0.65rem] text-ink-soft">
              {isAdmin ? ro.adminNotPlaying : ro.joinClosed}
            </Label>
            {isAdmin && <p className="text-sm text-ink-soft">{ro.cannotVoteAdmin}</p>}
          </main>
        )}

        {isAdmin && <AdminPanel snapshot={snapshot} />}
      </PhoneFrame>

      <ErrorToast error={room.error} onDismiss={room.dismissError} />
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Paper className="flex min-h-full flex-col items-center justify-center px-6 text-center">
      {children}
    </Paper>
  );
}
